import { useState, useEffect } from "react";
import { useApi } from "@/lib/useApi";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, EmptyState } from "@/components/common";
import { fDateTime } from "@/lib/format";
import { HeartPulse, AlertTriangle, CheckCircle2, Save, Settings2 } from "lucide-react";
import { toast } from "sonner";

function ConfigCard({ onSaved }) {
  const { t } = useLang();
  const { data } = useApi("/hr/absence-config");
  const [form, setForm] = useState({ threshold: 6, window_days: 180 });
  useEffect(() => { if (data) setForm({ threshold: data.threshold, window_days: data.window_days }); }, [data]);
  const save = async () => {
    try {
      await api.put("/hr/absence-config", { threshold: Number(form.threshold), window_days: Number(form.window_days) });
      toast.success(t("saved")); onSaved && onSaved();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Card className="mb-4 flex flex-wrap items-end gap-4 p-4" data-testid="absence-config">
      <div className="flex items-center gap-2 text-sm font-semibold"><Settings2 className="h-4 w-4 text-accent" />{t("am_cfg_title")}</div>
      <div><Label>{t("am_cfg_threshold")}</Label>
        <Input type="number" min="2" max="50" value={form.threshold} className="w-24"
          onChange={(e) => setForm((f) => ({ ...f, threshold: e.target.value }))} data-testid="cfg-threshold" /></div>
      <div><Label>{t("am_cfg_window")}</Label>
        <Input type="number" min="30" max="730" value={form.window_days} className="w-28"
          onChange={(e) => setForm((f) => ({ ...f, window_days: e.target.value }))} data-testid="cfg-window" /></div>
      <Button onClick={save} data-testid="cfg-save"><Save className="mr-1 h-4 w-4" />{t("save")}</Button>
    </Card>
  );
}

export default function Arbetsmiljo() {
  const { t } = useLang();
  const { data, reload } = useApi("/hr/absence-overview");
  const [handle, setHandle] = useState(null); // {employee_id, employee_name}
  const [note, setNote] = useState("");
  const items = data?.items || [];

  const openHandle = (r) => { setHandle(r); setNote(r.handle_note || ""); };
  const saveHandle = async (handled) => {
    try {
      await api.post(`/hr/absence-overview/${handle.employee_id}/handle`, { handled, note });
      toast.success(t("saved")); setHandle(null); setNote(""); reload();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <PageHeader title={t("am_title")} icon={HeartPulse} subtitle={t("am_sub")} />
      <ConfigCard onSaved={reload} />
      {items.length === 0 ? (
        <EmptyState icon={HeartPulse} title={t("am_empty_title")} text={t("am_empty_text")} />
      ) : (
        <Card className="overflow-x-auto kr-scroll" data-testid="absence-overview">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("am_col_employee")}</TableHead>
                <TableHead>{t("am_col_dept")}</TableHead>
                <TableHead>{t("am_col_occasions")}</TableHead>
                <TableHead>{t("am_col_last")}</TableHead>
                <TableHead>{t("am_col_manager")}</TableHead>
                <TableHead>{t("th_status")}</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((r) => (
                <TableRow key={r.employee_id} data-testid={`absence-row-${r.employee_id}`} className={r.handled ? "opacity-60" : ""}>
                  <TableCell className="font-medium">{r.employee_name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.department || "—"}</TableCell>
                  <TableCell className="font-mono font-bold">{r.occasions}</TableCell>
                  <TableCell className="font-mono text-xs">{r.last_absence ? fDateTime(r.last_absence) : "—"}</TableCell>
                  <TableCell className="text-sm">{r.manager_name || "—"}</TableCell>
                  <TableCell>
                    {r.handled
                      ? <Badge variant="secondary" className="gap-1" title={r.handle_note || ""}><CheckCircle2 className="h-3 w-3" />{t("am_handled_badge")}</Badge>
                      : r.flagged
                        ? <Badge variant="destructive" className="gap-1"><AlertTriangle className="h-3 w-3" />{t("am_flagged_badge")}</Badge>
                        : <Badge variant="secondary">{t("am_watch_badge")}</Badge>}
                  </TableCell>
                  <TableCell>
                    <Button size="sm" variant="outline" onClick={() => openHandle(r)} data-testid={`handle-${r.employee_id}`}>
                      {r.handled ? t("am_edit_action") : t("am_mark_action")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={!!handle} onOpenChange={(o) => !o && setHandle(null)}>
        <DialogContent data-testid="handle-dialog">
          <DialogHeader><DialogTitle>{t("am_action_title")} – {handle?.employee_name}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t("am_action_hint")}</p>
          {(handle?.actions || []).length > 0 && (
            <div className="max-h-40 space-y-1.5 overflow-y-auto rounded-md border p-2" data-testid="am-timeline">
              <div className="text-xs font-semibold">{t("am_timeline")}</div>
              {handle.actions.slice().reverse().map((a, i) => (
                <div key={i} className="text-xs">
                  <span className={a.handled ? "text-emerald-700" : "text-amber-700"}>●</span>{" "}
                  <span className="text-muted-foreground">{fDateTime(a.at)} · {a.by}</span>
                  {a.note && <div className="ml-3">{a.note}</div>}
                </div>
              ))}
            </div>
          )}
          <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("am_action_ph")} data-testid="handle-note" />
          {handle?.handled_by && <p className="text-xs text-muted-foreground">{t("am_last_handled", { by: handle.handled_by })}</p>}
          <DialogFooter>
            {handle?.handled && <Button variant="ghost" onClick={() => saveHandle(false)} data-testid="handle-reopen">{t("am_reopen")}</Button>}
            <Button onClick={() => saveHandle(true)} data-testid="handle-save"><CheckCircle2 className="mr-1 h-4 w-4" />{t("am_mark_done")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
